import type { App } from '../app/App';
import { NotificationPermissionPopup } from '../game/views/popups/notification/NotificationPermissionPopup';
import type { PopupStackController } from '../popups/PopupStackController';

/**
 * Checks if notification permission popup should be shown.
 * Uses RemoteNotificationHandler for Capacitor platforms.
 */
async function shouldShowNotificationPermissionPopup(app: App): Promise<boolean> {
  try {
    // Dynamically import RemoteNotificationHandler for Capacitor
    const remoteNotificationHandler = app.platform.notification;
    if (!remoteNotificationHandler) {
      return false;
    }
    
    return await remoteNotificationHandler.shouldShowNotificationPermissionPopup();
  } catch (error) {
    console.error('[NotificationPermission] Error checking if notification permission popup should be shown:', error);
    return false;
  }
}

/**
 * Opens the notification permission popup after waiting for all other popups to close.
 * This should be called after level completion when user returns to main menu.
 */
export async function openNotificationPermissionPopup(app: App): Promise<void> {
  const {
    controller: { popups },
  } = app as {
    controller: { popups: PopupStackController };
  };

  // Check if we should show the popup
  const shouldShow = await shouldShowNotificationPermissionPopup(app);
  if (!shouldShow) {
    return;
  }

  // Wait for all popups to close before showing notification permission popup
  await popups.waitUntilNoActivePopups();

  // Check if any popup opened while we were waiting (safety check)
  if (popups.isAnyPopupOpened()) {
    console.log('[NotificationPermission] Popup skipped - another popup opened while waiting');
    return;
  }

  const popup = new NotificationPermissionPopup();

  popup.onEvent.connect(async (event: string) => {
    if (event === 'okay') {
      await app.platform.notification?.handlePermissionAccepted();
      await popups.closeActivePopup();
    } else if (event === 'cancel') {
      await app.platform.notification?.handlePermissionDeclined();
      await popups.closeActivePopup();
    }
  });

  if (popups.isAnyPopupOpened()) {
    console.log('[NotificationPermission] Popup skipped - popup opened before display');
    return;
  }

  void popups.addPopup({
    popupInstance: popup,
    settings: { overlayFillOpacity: 0.7, hideRays: true, overlay: false },
  });
}

